import * as _ from "lodash"
import { WB_CustomerCalendarRow, WB_PeriodRow, WB_SourceRow } from "../PA.Core/PAdbSchemaRows"
import { DateUtils } from "../PA.Core/Basic/Dates"
import * as AsyncLock from "async-lock";
import { PAdbContext } from "../PA.Core/PAdbContext"
import { esWB_Customer_Calendar, esWB_Period, esWB_Source } from "../PA.Core/PAdbSchema"
import { IFiscalCalendarPeriods, IRetailerPromotionCalendar } from "../PA.MD.Core/PA.MD.IF.Calendar"
import { EnterpriseUtils } from "../PA.Core/Basic/EnterpriseUtils"
import { PeriodService } from "./PeriodService"
import { PASearch } from "../PA.Core/PASearch"



export class CalendarUtils {
    private static loadLock = new AsyncLock();
    private static dctRetailCalendars: {
        [wbSalesOrgId: number]: { [wbCustId: number]: IRetailerPromotionCalendar[] }
    } = {}
    private static dctFiscalCalendarPeriods: {
        [wbCompanyId: number]: { [entityTypeId: string]: IFiscalCalendarPeriods }
    } = {}


    static getWeekRangeDesc(startDOW: number){
        switch(startDOW){
            case 1: return "Sun-Sat"
            case 2: return "Mon-Sun"
            case 3: return "Tue-Mon"
            case 4: return "Wed-Tue"
            case 5: return "Thur-Wed"
            case 6: return "Fri-Thur"
            case 7: return "Sat-Fri"
            default: return ""
        }
    }

    static getDayAbbr(dow: number){
        switch(dow){
            case 1: return "Sun"
            case 2: return "Mon"
            case 3: return "Tue"
            case 4: return "Wed"
            case 5: return "Thur"
            case 6: return "Fri"
            case 7: return "Sat"
            default: return ""
        }
    }

    static convertStringDOWToIntegerDOW(sValue: string | number, dftDOW: number): number {
        switch(sValue + ''){
            case "1": return 1
            case "2": return 2
            case "3": return 3
            case "4": return 4
            case "5": return 5
            case "6": return 6
            case "7": return 7
            case "Sunday": return 1
            case "Monday": return 2
            case "Tuesday": return 3
            case "Wednesday": return 4
            case "Thursday": return 5
            case "Friday": return 6
            case "Saturday": return 7
        }
        return dftDOW
    }



    static resetRetailerCalendarCache(wbSalesOrgId: number): void {
        CalendarUtils.dctRetailCalendars[wbSalesOrgId] = {}
    }

    static hasLoadedRetailCalendars(wbSalesOrgId: number, wbCustId: number): boolean {
        if (wbSalesOrgId in CalendarUtils.dctRetailCalendars) {
            if (wbCustId in CalendarUtils.dctRetailCalendars[wbSalesOrgId])
                return _.isArray(CalendarUtils.dctRetailCalendars[wbSalesOrgId][wbCustId])
        }
        return false
    }

    static getRetailCalendars(wbSalesOrgId: number, wbCustIds: number[], startDt: Date, endDt?: Date): IRetailerPromotionCalendar[] {
        let rv: IRetailerPromotionCalendar[] = []

        let custCalcs = CalendarUtils.dctRetailCalendars[wbSalesOrgId] || {}

        _.forEach(wbCustIds, (wbCustId) => {
            if (wbCustId in custCalcs) {
                _.forEach(custCalcs[wbCustId] || [], (rc) => {
                    if (DateUtils.RangeDatesOverlap(rc.effectiveStartDt, rc.effectiveEndDt, startDt, endDt))
                        rv.push(rc)
                })
            }
        })

        return rv
    }


    static async loadRetailCalendars(wbSalesOrgId: number, wbCustIds: number[]): Promise<void> {
        let dbContext = new PAdbContext();
        for (let wbCustId of wbCustIds) {
            try {
                let lockKey = `${wbSalesOrgId}-${wbCustId}`
                await CalendarUtils.loadLock.acquire<void>(lockKey, async (done) => {
                    try {
                        const rpcs= await CalendarUtils.retrieveCustomerRetailCalendars(dbContext, wbSalesOrgId, wbCustId)

                        CalendarUtils.dctRetailCalendars[wbSalesOrgId] ||= {}
                        CalendarUtils.dctRetailCalendars[wbSalesOrgId][wbCustId] = _.orderBy(rpcs, (c) => c.effectiveStartDt)
                        done()
                    } catch (err) {
                        done(err)
                    }
                })
            } catch (err) {
                console.log(err)
            }
        } // Next Customer
    }

    static async retrieveCustomerRetailCalendars(dbContext: PAdbContext, wbSalesOrgId: number, wbCustId: number): Promise<IRetailerPromotionCalendar[]> {
        let arrCustCal = <WB_CustomerCalendarRow[]>await dbContext.findAll(esWB_Customer_Calendar, {
            where: {
                WB_SALESORG_ID: wbSalesOrgId,
                WB_CUST_ID: wbCustId,
                DELETED_IND: { neq: '1' }
            }
        })
        let arrSourceIds = _.map(_.filter(arrCustCal, (a) => a.cif.WB_SOURCE_ID > 0), (b) => b.cif.WB_SOURCE_ID)
        let arrSource = <WB_SourceRow[]>await dbContext.findUsingCache(esWB_Source, arrSourceIds)
        let dctSource = _.keyBy(arrSource, (s) => s.cif.WB_SOURCE_ID)

        const rpcs: IRetailerPromotionCalendar[] = []
        _.forEach(arrCustCal, (cc) => {
            let prdSrc = dctSource[cc.cif.WB_SOURCE_ID]
            let providerStartDOW = CalendarUtils.convertStringDOWToIntegerDOW(prdSrc?.cif?.WEEK_START_DAY_OF_WEEK, 1) // Sunday
            let providerStartDayOfWeekActual =  CalendarUtils.convertStringDOWToIntegerDOW(cc.cif.WEEK_START_DAY_OF_WEEK_ACT,1) // Sunday

            let rpc: IRetailerPromotionCalendar = {
                name: "Retailer Calendar",
                wbSalesOrgId: wbSalesOrgId,
                wbCustId: wbCustId,
                startDayOfWeek: CalendarUtils.convertStringDOWToIntegerDOW(cc.cif.RETAIL_CALENDAR_START, 7), // Saturday
                consumptionData: {
                    dataLevel: cc.cif.CONSUMPTION_PERIOD_LEVEL_ID,
                    providerDataStartDayOfWeekActual: providerStartDayOfWeekActual,
                    dataOffset: (cc.cif.CONSUMPTION_PERIOD_LEVEL_ID == 'Day') ? 0 : DateUtils.DayOfWeek_findNearestOffset(providerStartDOW, providerStartDayOfWeekActual),
                    dataOffsetTableRefValue: cc.cif.CONSUMPTION_DAY_OFFSET,
                    providerWbSourceId: cc.cif.WB_SOURCE_ID,
                },
                effectiveStartDt: cc.EffectiveStartDt,
                effectiveEndDt: cc.EffectiveEndDt,
                consumptionDataProvider: prdSrc,
                customerCalendarRow: cc
            }

            rpcs.push(rpc)
        })
        return rpcs
    }

    // Fiscal Hierarchy
    static resetFiscalCalendarCache(wbCompanyId: number): void {
        CalendarUtils.dctFiscalCalendarPeriods[wbCompanyId] = {}
    }

    static hasLoadedFiscalCalendarLevelNodes(wbCompanyId: number, entityTypeId: string): boolean {
        if (wbCompanyId in CalendarUtils.dctFiscalCalendarPeriods) {
            if (entityTypeId in CalendarUtils.dctFiscalCalendarPeriods[wbCompanyId])
                return !_.isEmpty(CalendarUtils.dctFiscalCalendarPeriods[wbCompanyId][entityTypeId])
        }
        return false
    }

    static getFiscalCalendarLevelNodes(wbCompanyId: number, entityTypeId: string): IFiscalCalendarPeriods {

        let lvl = CalendarUtils.dctFiscalCalendarPeriods[wbCompanyId] || {}

        return lvl[entityTypeId]
    }

    static async loadSalesOrgFiscalCalendarLevelNodes(wbSalesOrgId: number, entityTypeId: string): Promise<void> {
        let so = await EnterpriseUtils.getSalesOrg(wbSalesOrgId)
        await CalendarUtils.loadSalesOrgFiscalCalendarLevelNodes(so?.cif?.WB_COMPANY_ID || 0, entityTypeId)
    }

    static async loadFiscalCalendarLevelNodes(wbCompanyId: number, entityTypeId: string): Promise<void> {
        let dbContext = new PAdbContext();

        let lockKey = `${wbCompanyId}-${entityTypeId}`
        await CalendarUtils.loadLock.acquire<void>(lockKey, async (done) => {
            try {

                let co = await EnterpriseUtils.getCompany(wbCompanyId)
                let fc: IFiscalCalendarPeriods
                if((!co) || (!(co?.cif?.WB_PERIODH_ID > 0))){
                    fc = {
                        name: `Company ${wbCompanyId}`,
                        wbCompanyId: wbCompanyId,
                        periodTypeId: entityTypeId,
                        periods: []
                    }

                    let periods = await PeriodService.getPeriodsByPeriodTypeIDDateRange(entityTypeId, DateUtils.convertFromSAPBW("20000101"), DateUtils.convertFromSAPBW("20990101"))
                    periods = _.orderBy(periods, (p) => p.PeriodStartDt)
                    _.forEach(periods, (p) => {
                        fc.periods.push({
                            wbPeriodId: p.cif.WB_PERIOD_ID,
                            periodId: p.cif.PERIOD_ID,
                            periodName: p.cif.PERIOD_NAME,
                            periodStart: p.cif.PERIOD_START_DT,
                            periodEnd: p.cif.PERIOD_END_DT,
                            periodStartDt: p.PeriodStartDt,
                            periodEndDt: p.PeriodEndDt
                        })
                    })
                } else {
                    fc = {
                        name: `${co.cif.COMPANY_NAME} ${co.cif.COMPANY_ID}`,
                        wbCompanyId: wbCompanyId,
                        periodTypeId: entityTypeId,
                        periods: []
                    }

                    const vSearchSchema = PASearch.createSimpleSchema(["ENTITY_ID","ENTITY_NAME","PERIOD_ID","PERIOD_NAME"], ["WB_PERIODH_ID", "WB_PERIOD_ID", "WB_ENTITY_ID", "WB_ENTITY_ID"])
                    const arrPeriodSearchRows = await PASearch.find("vUI_Period_Hierarchy_Entity_Search",
                        {
                            where: {
                                WB_PERIODH_ID: co.cif.WB_PERIODH_ID,
                                ENTITY_ID: entityTypeId
                            }
                        }, vSearchSchema, [], 0, ["WB_PERIOD_ID"])
                    const arrWbPeriodIds = <number[]>_.map(arrPeriodSearchRows, (row) => row.WB_PERIOD_ID)
                    let allPeriods = <WB_PeriodRow[]>await dbContext.findUsingCache(esWB_Period, arrWbPeriodIds)

                    allPeriods = _.sortBy(allPeriods, (a) => a.cif.PERIOD_START_DT)
                    _.forEach(allPeriods, (p) => {
                        fc.periods.push({
                            wbPeriodId: p.cif.WB_PERIOD_ID,
                            periodId: p.cif.PERIOD_ID,
                            periodName: p.cif.PERIOD_NAME,
                            periodStart: p.cif.PERIOD_START_DT,
                            periodEnd: p.cif.PERIOD_END_DT,
                            periodStartDt: p.PeriodStartDt,
                            periodEndDt: p.PeriodEndDt
                        })
                    })
                }

                CalendarUtils.dctFiscalCalendarPeriods[wbCompanyId] ||= {}
                CalendarUtils.dctFiscalCalendarPeriods[wbCompanyId][entityTypeId] = fc
                done()
            } catch (err) {
                done(err)
            }
        })

    }

}